// AGC: BiometricLockScreen - Full-screen overlay for biometric unlock
// AGC: Mirrors Yitix plan - shown on fresh app open when biometric is enabled
// AGC: Uses NativeBiometric.verifyIdentity (no stored credentials needed)

import React, { useEffect, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { NativeBiometric } from '@capgo/capacitor-native-biometric'
import { Fingerprint, ScanFace, LogIn } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import { useTheme } from '../../context/ThemeContext'

interface BiometricLockScreenProps {
  onUnlock: () => void
  onManualLogin: () => void
}

/**
 * AGC: BiometricLockScreen - Device lock overlay
 * AGC: Auto-triggers verifyIdentity on mount; Try Again or Manual Login on failure
 */
export const BiometricLockScreen: React.FC<BiometricLockScreenProps> = ({
  onUnlock,
  onManualLogin,
}) => {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const [error, setError] = useState<string | null>(null)
  const [isVerifying, setIsVerifying] = useState(true)

  const verifyIdentity = async () => {
    if (!Capacitor.isNativePlatform()) {
      onUnlock()
      return
    }
    setError(null)
    setIsVerifying(true)
    try {
      await NativeBiometric.verifyIdentity({
        reason: t('biometric.unlockMessage') || 'Authenticate to continue',
        title: t('biometric.unlockTitle') || 'Unlock App',
        subtitle: 'Droidfarm',
        description: t('biometric.unlockMessage') || 'Authenticate to continue',
      })
      setIsVerifying(false)
      onUnlock()
    } catch (err: any) {
      setIsVerifying(false)
      const msg = err?.message || String(err)
      if (msg.includes('cancel') || err?.code === 'BIOMETRIC_CANCELED') {
        setError(t('biometric.failedMessage') || 'Authentication failed. Try again or use manual login.')
      } else {
        setError(t('biometric.failedMessage') || 'Authentication failed. Try again or use manual login.')
      }
    }
  }

  useEffect(() => {
    verifyIdentity()
  }, [])

  const Icon = () => (
    <div
      className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
      style={{ backgroundColor: 'var(--accent-light-bg)' }}
    >
      <ScanFace className="w-10 h-10" style={{ color: 'var(--accent-primary)' }} />
    </div>
  )

  if (isVerifying && !error) {
    return (
      <div
        className="fixed inset-0 flex flex-col items-center justify-center p-6 z-[9999]"
        style={{
          backgroundColor: isDark ? '#0f172a' : '#f8fafc',
        }}
      >
        <Icon />
        <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2 text-center">
          {t('biometric.unlockTitle')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
          {t('biometric.unlockMessage')}
        </p>
        <div className="animate-pulse text-gray-500">...</div>
      </div>
    )
  }

  return (
    <div
      className="fixed inset-0 flex flex-col items-center justify-center p-6 z-[9999]"
      style={{
        backgroundColor: isDark ? '#0f172a' : '#f8fafc',
      }}
    >
      <Icon />
      <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2 text-center">
        {t('biometric.unlockTitle')}
      </h1>
      {error && (
        <p className="text-red-600 dark:text-red-400 text-center mb-6">
          {error}
        </p>
      )}
      <div className="flex flex-col gap-3 w-full max-w-xs">
        <button
          onClick={verifyIdentity}
          className="w-full py-3 px-4 rounded-xl font-medium text-white transition-opacity hover:opacity-90"
          style={{ backgroundColor: 'var(--accent-primary)' }}
        >
          {t('biometric.tryAgain')}
        </button>
        <button
          onClick={onManualLogin}
          className="w-full py-3 px-4 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          {t('biometric.useManualLogin')}
        </button>
      </div>
    </div>
  )
}

export default BiometricLockScreen
